import React, { useState, useRef } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Trash2
} from 'lucide-react';

const UIComponents = ({ layout, includeTopPanel, includeBottomPanel, components, setComponents }) => {
  const [newSections, setNewSections] = useState({
    main: '',
    sidebar: '',
    left: '',
    right: '',
    top: '',
    bottom: ''
  });
  
  // Use refs to maintain scroll position and focus
  const containerRef = useRef(null);

  const getLayoutSections = () => {
    const sections = [];
    
    // Add top panel if included
    if (includeTopPanel) {
      sections.push({ id: 'top', label: 'FULL-WIDTH TOP PANEL' });
    }
    
    // Add core layout sections
    switch (layout) {
      case 'single-page':
        sections.push({ id: 'main', label: 'SINGLE-PAGE' });
        break;
      case 'left-sidebar':
        sections.push({ id: 'sidebar', label: 'SIDEBAR' });
        sections.push({ id: 'main', label: 'MAIN AREA' });
        break;
      case 'right-sidebar':
        sections.push({ id: 'main', label: 'MAIN AREA' });
        sections.push({ id: 'sidebar', label: 'SIDEBAR' });
        break;
      case 'split-screen':
        sections.push({ id: 'left', label: 'LEFT SIDE' });
        sections.push({ id: 'right', label: 'RIGHT SIDE' });
        break;
      default:
        sections.push({ id: 'main', label: 'MAIN' });
    }
    
    // Add bottom panel if included
    if (includeBottomPanel) {
      sections.push({ id: 'bottom', label: 'FULL-WIDTH BOTTOM PANEL' });
    }
    
    return sections;
  };

  const handleSaveComponent = (sectionKey) => {
    if (!newSections[sectionKey].trim()) return;

    const newComponent = {
      id: Date.now(),
      content: newSections[sectionKey],
      section: sectionKey
    };

    // Update components while preserving focus
    setComponents([...components, newComponent]);
    
    // Reset only the specific section input
    setNewSections(prev => ({
      ...prev,
      [sectionKey]: ''
    }));
  };

  const handleInputChange = (sectionKey, value) => {
    setNewSections(prev => ({
      ...prev,
      [sectionKey]: value
    }));
  };

  const handleMoveComponent = (index, direction, sectionKey) => {
    const sectionComponents = components.filter(comp => comp.section === sectionKey);
    
    // Create a copy of the section components
    const updatedSectionComponents = [...sectionComponents];
    
    // Swap components
    const temp = updatedSectionComponents[index];
    updatedSectionComponents[index] = updatedSectionComponents[index + direction];
    updatedSectionComponents[index + direction] = temp;
    
    // Update the main components array
    const otherComponents = components.filter(comp => comp.section !== sectionKey);
    setComponents([...otherComponents, ...updatedSectionComponents]);
  };

  const handleDeleteComponent = (componentId, sectionKey) => {
    setComponents(components.filter(comp => !(comp.section === sectionKey && comp.id === componentId)));
  };

  const renderSingleSection = (section) => {
    const sectionComponents = components.filter(comp => comp.section === section.id);
    const isFullWidth = section.id === 'top' || section.id === 'bottom';

    return (
      <div 
        key={section.id}
        className={`bg-white rounded-lg shadow-md p-6 ${isFullWidth ? 'col-span-full' : ''}`}
      >
        <div className="text-sm font-medium text-gray-600 uppercase mb-4 border-b pb-2">
          {section.label}
        </div>

        <div className="space-y-4">
          {sectionComponents.map((component, index) => (
            <div key={component.id} className="bg-gray-50 rounded-lg p-4 flex items-start gap-4">
              <p className="flex-grow">{component.content}</p>
              <div className="flex gap-2">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleMoveComponent(index, -1, section.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                )}
                {index < sectionComponents.length - 1 && (
                  <button
                    type="button"
                    onClick={() => handleMoveComponent(index, 1, section.id)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDeleteComponent(component.id, section.id)}
                  className="p-1 hover:bg-gray-100 rounded text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <div className="space-y-2 mt-4">
            <textarea
              value={newSections[section.id]}
              onChange={(e) => handleInputChange(section.id, e.target.value)}
              placeholder="Describe a new UI element or content section..."
              className="w-full p-3 border rounded-md"
              rows={3}
            />
            <button
              type="button"
              onClick={() => handleSaveComponent(section.id)}
              disabled={!newSections[section.id].trim()}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Add Section
            </button>
          </div>
        </div>
      </div>
    );
  };

  const sections = getLayoutSections();
  const isMultiColumn = layout !== 'single-page' && layout !== 'other';
  
  // Separate sections for rendering
  const topSection = sections.find(s => s.id === 'top');
  const bottomSection = sections.find(s => s.id === 'bottom');
  const mainSections = sections.filter(s => s.id !== 'top' && s.id !== 'bottom');

  return (
    <div className="space-y-6" ref={containerRef}>
      {/* Top panel (if any) */}
      {topSection && (
        <div className="grid grid-cols-1 gap-4">
          {renderSingleSection(topSection)}
        </div>
      )}

      {/* Main layout sections (one or two columns) */}
      <div className={`grid ${isMultiColumn ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
        {mainSections.map(section => renderSingleSection(section))}
      </div>

      {/* Bottom panel (if any) */}
      {bottomSection && (
        <div className="grid grid-cols-1 gap-4">
          {renderSingleSection(bottomSection)}
        </div>
      )}
    </div>
  );
};

export default UIComponents;